import { useQuery } from '@tanstack/react-query';
import { concat, isEmpty } from 'lodash';
import {
  useCallback,
  useContext,
  useEffect,
  useState,
  useTransition,
} from 'react';
import { useLocation } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  SortMenuOption,
  SortOptions,
} from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { useGetComponentTypesWithPagination, useParamQuery } from 'src/hooks';
import { fetchComponentList } from 'src/hooks/api/components/useGetComponents';
import { useGetAllComponentTags } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { IComponent, IComponentType } from 'src/interfaces';
import { formatQuery, queryKeys } from 'src/utils';

export const useListComponentsPage = (filters?: string) => {
  const { userRole } = useContext(AuthDataProvider);
  const [isLoadingSidebarPreview, startTransition] = useTransition();

  const [selectedCard, setSelectedCard] = useState<IComponent | null>(null);
  const [hasSidebarPreviewLoaded, setHasSidebarPreviewLoaded] = useState(false);

  const handleCardClick = useCallback((component: IComponent) => {
    setSelectedCard(component);

    startTransition(() => {
      setHasSidebarPreviewLoaded(true);
    });
  }, []);

  const onHandleComponentPreviewSidebarClose = useCallback(() => {
    setSelectedCard(null);
    setHasSidebarPreviewLoaded(false);
  }, []);

  const location = useLocation();
  const { activeFilters, getParamsValue, onChangeParams } = useParamQuery({
    sort: [`-${SortOptions.DATE_CREATED}`],
    page: ['1'],
    pageSize: ['20'],
  });

  // Get current page from URL params
  const pageParam = Number(getParamsValue('page')?.[0] || 1);
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  // Get page size from URL params
  const pageSizeParam = Number(getParamsValue('pageSize')?.[0] || 20);
  const pageSize = [20, 50, 100].includes(pageSizeParam) ? pageSizeParam : 20;

  const {
    data: componentsData,
    isLoading: isLoadingComponents,
    error: errorLoadingComponents,
  } = useQuery({
    queryKey: queryKeys.components.pagination(
      formatQuery(location.search + (filters ? '&' + filters : '')),
    ),
    queryFn: () =>
      fetchComponentList(
        concat(formatQuery(location.search + (filters ? '&' + filters : ''))),
        true,
      ),
    enabled: !isEmpty(formatQuery(location.search)),
  });

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    onChangeParams('page', [`${value}`]);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    onChangeParams('pageSize', [`${newPageSize}`]);
    onChangeParams('page', ['1']); // Reset to first page when changing page size
  };

  // sorting
  const selectedSortOption: SortMenuOption = {
    option: getParamsValue('sort')[0]?.replace('-', '') as SortOptions,
    order: getParamsValue('sort')[0]?.includes('-') ? 'desc' : 'asc',
  };

  const onSortChange = useCallback(
    (selectedOption: { option: SortOptions; order: 'desc' | 'asc' }) => {
      onChangeParams('sort', [
        `${selectedOption.order === 'desc' ? '-' : ''}${selectedOption.option}`,
      ]);
      onHandleComponentPreviewSidebarClose();
    },
    [onChangeParams, onHandleComponentPreviewSidebarClose],
  );

  const components = componentsData?.data ?? [];
  const totalPages = componentsData?.totalPages ?? 0;
  const totalCount = componentsData?.totalCount ?? 0;
  const hasActiveFilters = !isEmpty(activeFilters);

  useEffect(() => {
    if (
      !isLoadingComponents &&
      page > 1 &&
      components.length === 0 &&
      totalCount > 0
    ) {
      onChangeParams('page', [`${page - 1}`]);
    } else if (!isLoadingComponents && totalPages > 0 && page > totalPages) {
      // Current page exceeds total pages - navigate to last valid page
      onChangeParams('page', [`${totalPages}`]);
    }
  }, [
    isLoadingComponents,
    page,
    totalPages,
    components.length,
    totalCount,
    onChangeParams,
  ]);

  // Components type filter data with pagination
  const [componentTypesPage, setComponentTypesPage] = useState({
    data: [] as IComponentType[],
    page: 1,
    totalPages: 1,
  });

  const {
    data: componentTypesData,
    isLoading: isLoadingComponentTypes,
    error: errorLoadingComponentTypes,
  } = useGetComponentTypesWithPagination([`page=${componentTypesPage.page}`], {
    staleTime: Infinity,
  });

  const { data: componentTags = [] } = useGetAllComponentTags(userRole);

  useEffect(() => {
    if (componentTypesData && componentTypesData.data) {
      setComponentTypesPage({
        ...componentTypesPage,
        data: [...componentTypesPage.data, ...componentTypesData.data],
        totalPages: componentTypesData.totalPages,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentTypesData, setComponentTypesPage]);

  const updateComponentTypesPage = (page: number) => {
    setComponentTypesPage({ ...componentTypesPage, page });
  };

  return {
    isLoadingComponents,
    errorLoadingComponents,
    selectedSortOption,
    onSortChange,
    hasActiveFilters,
    componentTypesPage,
    isLoadingComponentTypes,
    errorLoadingComponentTypes,
    updateComponentTypesPage,
    components,
    totalCount,
    totalPages,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    selectedCard,
    handleCardClick,
    hasSidebarPreviewLoaded,
    isLoadingSidebarPreview,
    onHandleComponentPreviewSidebarClose,
    componentTags,
    activeFilters,
  };
};
